/**
 * footerTemplates.js
 *
 * Vorgefertigte Footer-Templates, die in der Multi-Footer-Übersicht als
 * horizontale Slideshow erscheinen, solange noch kein Footer mit echtem
 * Inhalt existiert. Klick auf ein Template legt einen neuen Footer mit
 * passenden Demo-Daten an — der User muss nur noch anpassen.
 *
 * Schema spiegelt EXAKT die Felder, die `EmailFooterGenerator.js` und
 * `WorldSettings.js → renderFooterEditView` lesen. Daher: jedes Template
 * funktioniert ohne weitere Migration.
 */

/**
 * Asset-Strategie für maximale E-Mail-Client-Kompatibilität:
 *
 *   1) Social-Icons werden als PNG aus `public/img/footer-templates/` ausgeliefert
 *      (96×96 RGBA, ~1–6 KB pro Datei). PNG rendert in JEDEM relevanten Mail-Client
 *      (Gmail Web/Mobile, Outlook 2016+, Apple Mail, Yahoo). SVG wird von vielen
 *      Clients gestrippt — daher bewusst kein simpleicons-CDN mehr.
 *
 *   2) Die URLs sind absolut auf den Firebase-Hosting-Origin gesetzt, damit der
 *      Empfänger die Bilder über öffentliches HTTPS lädt. Wir nutzen die
 *      Default-Hosting-Domain (`bonusclub-ab5e2.web.app`), die ohne DNS-Konfig
 *      garantiert verfügbar ist; eine eigene Domain (z. B. bonusclub.link)
 *      würde ebenfalls funktionieren, ist aber nicht erforderlich.
 *
 *   3) Der Logo-Platzhalter im "Logo & Signature"-Template ist ein Inline-SVG
 *      als data-URI. Damit gibt es im Editor-Preview KEINEN externen Request,
 *      und das Layout wirkt sofort ruhig & on-brand. Der Platzhalter ist nur
 *      für den Editor gedacht — der User ersetzt ihn vor dem Versand durch
 *      sein eigenes Logo (Upload-Pfad existiert bereits in `WorldSettings.js`).
 */
const ASSET_BASE = 'https://bonusclub-ab5e2.web.app/img/footer-templates';
const ICON_LINKEDIN = `${ASSET_BASE}/linkedin.png`;
const ICON_INSTAGRAM = `${ASSET_BASE}/instagram.png`;
const ICON_X = `${ASSET_BASE}/x.png`;

// Neutraler, on-brand Logo-Platzhalter (200×200, runde Ecken, weiches Gradient,
// "LOGO"-Schriftzug). Inline-SVG → data-URI, kein externer Request.
const LOGO_PLACEHOLDER_SVG = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200' width='200' height='200'>
  <defs>
    <linearGradient id='bg' x1='0' y1='0' x2='1' y2='1'>
      <stop offset='0%' stop-color='#EFF6FF'/>
      <stop offset='100%' stop-color='#E0F2F1'/>
    </linearGradient>
  </defs>
  <rect width='200' height='200' rx='24' fill='url(#bg)'/>
  <circle cx='100' cy='80' r='30' fill='#2A6776' opacity='0.92'/>
  <text x='100' y='88' text-anchor='middle' font-family='-apple-system,Segoe UI,Helvetica,Arial,sans-serif' font-size='20' font-weight='700' fill='#ffffff' letter-spacing='1'>LOGO</text>
  <rect x='52' y='128' width='96' height='8' rx='4' fill='#2A6776' opacity='0.40'/>
  <rect x='72' y='146' width='56' height='6' rx='3' fill='#2A6776' opacity='0.22'/>
</svg>`;
const LOGO_PLACEHOLDER = `data:image/svg+xml;utf8,${encodeURIComponent(LOGO_PLACEHOLDER_SVG)}`;

const EMPTY_ICON = { iconUrl: '', link: '', size: 32 };
const fillIcons = (icons) => {
  const out = [...icons];
  while (out.length < 4) out.push({ ...EMPTY_ICON });
  return out;
};

/**
 * Liste aller Templates. Reihenfolge = Anzeigereihenfolge in der Slideshow.
 * Jedes Template enthält UI-Metadaten (label, description, accent) UND ein
 * `footer`-Objekt mit allen Feldern, die der Footer-Generator versteht.
 */
export const FOOTER_TEMPLATES = [
  {
    id: 'minimal',
    label: 'Minimal Pro',
    description: 'Klare Text-Signatur — Name, Rolle, Kontakt. Kein Bild, keine Icons.',
    accent: '#2a6776',
    footer: {
      name: 'Minimal Pro',
      footer_text: [
        '<p style="margin:0 0 4px 0;font-weight:600;color:#1e293b;">Max Mustermann</p>',
        '<p style="margin:0 0 4px 0;color:#6b7280;">Founder &amp; CEO</p>',
        '<p style="margin:0 0 4px 0;color:#6b7280;">Nordwind Consulting GmbH</p>',
        '<p style="margin:8px 0 0 0;color:#94a3b8;font-size:11px;">+49 89 123 45 67 · max@nordwind-consulting.com</p>',
      ].join(''),
      footer_image_url: '',
      footer_image_position: 'below',
      footer_font: 'Helvetica Neue',
      footer_image_size: 80,
      socialIcons: fillIcons([]),
      socialIconsPosition: 'left',
      socialIconsMarginTop: 15,
      socialIconsMarginBottom: 10,
      optOutEnabled: true,
      optOutText: 'Unsubscribe',
    },
  },
  {
    id: 'with-logo',
    label: 'Logo & Signature',
    description: 'Logo links, Signatur rechts daneben. Klassischer Business-Look.',
    accent: '#6897B7',
    footer: {
      name: 'Logo & Signature',
      footer_text: [
        '<p style="margin:0 0 4px 0;font-weight:600;color:#1e293b;">Max Mustermann</p>',
        '<p style="margin:0 0 4px 0;color:#6b7280;">Head of Sales · Nordwind Consulting</p>',
        '<p style="margin:8px 0 0 0;color:#94a3b8;font-size:11px;">max@nordwind-consulting.com · www.nordwind-consulting.com</p>',
      ].join(''),
      footer_image_url: LOGO_PLACEHOLDER,
      footer_image_position: 'beside',
      footer_font: 'Helvetica Neue',
      footer_image_size: 70,
      socialIcons: fillIcons([]),
      socialIconsPosition: 'left',
      socialIconsMarginTop: 12,
      socialIconsMarginBottom: 8,
      optOutEnabled: true,
      optOutText: 'Unsubscribe',
    },
  },
  {
    id: 'social-connect',
    label: 'Social Connect',
    description: 'Signatur plus eine Reihe Social-Icons (LinkedIn, Instagram, X).',
    accent: '#0A66C2',
    footer: {
      name: 'Social Connect',
      footer_text: [
        '<p style="margin:0 0 4px 0;font-weight:600;color:#1e293b;">Max Mustermann</p>',
        '<p style="margin:0 0 8px 0;color:#6b7280;">Founder · Nordwind Consulting GmbH</p>',
      ].join(''),
      footer_image_url: '',
      footer_image_position: 'below',
      footer_font: 'Helvetica Neue',
      footer_image_size: 80,
      socialIcons: fillIcons([
        { iconUrl: ICON_LINKEDIN, link: 'https://linkedin.com/company/nordwind', size: 28 },
        { iconUrl: ICON_INSTAGRAM, link: 'https://instagram.com/nordwind', size: 28 },
        { iconUrl: ICON_X, link: 'https://x.com/nordwind', size: 28 },
      ]),
      socialIconsPosition: 'left',
      socialIconsMarginTop: 12,
      socialIconsMarginBottom: 12,
      optOutEnabled: true,
      optOutText: 'Unsubscribe',
    },
  },
];

/**
 * Erzeugt aus einer Template-ID ein frisches, persistierbares Footer-Objekt.
 * Setzt eine eindeutige `id` und `createdAt`, damit es in `footerList`
 * unmittelbar gespeichert werden kann.
 */
export function buildFooterFromTemplate(templateId) {
  const tpl = FOOTER_TEMPLATES.find((t) => t.id === templateId);
  if (!tpl) return null;
  return {
    ...tpl.footer,
    id: `footer_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    isActive: true,
    createdAt: Date.now(),
  };
}

/**
 * Heuristik: gilt als „echter" Footer, sobald irgendein content-Feld gefüllt
 * ist. Default-Slot mit komplett leeren Feldern → false → Slideshow zeigen.
 */
export function isFooterMeaningful(footer) {
  if (!footer) return false;
  const text = (footer.footer_text || '').replace(/<[^>]*>/g, '').trim();
  if (text.length > 0) return true;
  if (footer.footer_image_url && String(footer.footer_image_url).trim().length > 0) return true;
  const icons = Array.isArray(footer.socialIcons) ? footer.socialIcons : [];
  if (icons.some((i) => i && i.iconUrl && i.link)) return true;
  return false;
}
